import { useMutation, useQuery } from '@tanstack/react-query';
import { Briefcase, Check, Loader2, Search, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type JobView, type PipelineView } from '../../lib/client.ts';
import { addCandidates, type AddResult } from '../../lib/shortlists.ts';
import { cn } from '../../lib/utils.ts';
import { useDebounced } from '../record-table/use-debounced.ts';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { Input } from '../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';

/** Stage + client visibility: the two choices both dialogs ask before adding. */
function PlacementOptions({
  pipeline,
  stageId,
  onStage,
  visible,
  onVisible,
}: {
  pipeline: PipelineView | undefined;
  stageId: string;
  onStage: (id: string) => void;
  visible: boolean;
  onVisible: (v: boolean) => void;
}) {
  const open = (pipeline?.stages ?? []).filter((s) => !s.isTerminal);
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {open.length > 0 && (
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Starting stage
          <Select value={stageId || open[0]!.id} onValueChange={onStage}>
            <SelectTrigger aria-label="Starting stage">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {open.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      )}
      <label className="flex items-center gap-2 self-end pb-2.5 text-sm font-medium">
        <input
          type="checkbox"
          className="accent-brand size-4"
          checked={visible}
          onChange={(e) => onVisible(e.target.checked)}
        />
        The client can see them
      </label>
    </div>
  );
}

const fullName = (c: {
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  email?: string | null | undefined;
}) => [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Unnamed';

/**
 * Job → shortlist → "Add candidates" (TS-1599): search the talent pool, tick one or several,
 * pick the stage they start in and whether the client sees them. People already on this
 * shortlist are shown as such rather than offered again.
 */
export function AddCandidatesDialog({
  shortlist,
  jobTitle,
  pipeline,
  onClose,
  onDone,
}: {
  shortlist: { id: string; name: string; candidates: ReadonlyArray<{ candidateId: string }> };
  jobTitle: string;
  pipeline: PipelineView | undefined;
  onClose: () => void;
  onDone: (result: AddResult) => void;
}) {
  const [q, setQ] = useState('');
  const query = useDebounced(q.trim(), 250);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [stageId, setStageId] = useState('');
  const [visible, setVisible] = useState(false);
  const already = new Set(shortlist.candidates.map((c) => c.candidateId));
  const results = useQuery({
    queryKey: ['shortlist-candidate-picker', query],
    queryFn: () =>
      api.candidates.search({
        limit: 20,
        fields: ['firstName', 'lastName', 'email', 'currentRole'],
        ...(query ? { q: query } : {}),
      }),
  });
  const add = useMutation({
    mutationFn: () =>
      addCandidates(shortlist.id, [...picked], { stageId, visibleToClient: visible }),
    onSuccess: onDone,
  });
  const toggle = (id: string) =>
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const items = results.data?.items ?? [];
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add candidates</DialogTitle>
          <DialogDescription>
            To {shortlist.name} on {jobTitle}.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2" />
          <Input
            autoFocus
            aria-label="Search candidates"
            placeholder="Search by name, email or role…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10"
          />
        </div>
        <ul
          className="border-border max-h-72 divide-y overflow-y-auto rounded-xl border"
          data-testid="candidate-picker"
        >
          {results.isPending && (
            <li className="text-muted-foreground flex items-center gap-2 px-4 py-6 text-sm">
              <Loader2 className="size-4 animate-spin" /> Searching…
            </li>
          )}
          {results.isError && (
            <li className="text-danger px-4 py-6 text-sm" role="alert">
              {describeError(results.error)}
            </li>
          )}
          {results.data && items.length === 0 && (
            <li className="text-muted-foreground px-4 py-6 text-center text-sm">
              No candidates match.
            </li>
          )}
          {items.map((c) => {
            const on = already.has(c.id);
            const checked = picked.has(c.id);
            return (
              <li key={c.id}>
                <label
                  className={cn(
                    'flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                    on ? 'opacity-60' : 'hover:bg-surface-hover cursor-pointer',
                    checked && 'bg-brand-soft/60',
                  )}
                >
                  <input
                    type="checkbox"
                    className="accent-brand size-4 shrink-0"
                    disabled={on}
                    checked={on || checked}
                    onChange={() => toggle(c.id)}
                    aria-label={`Pick ${fullName(c)}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{fullName(c)}</span>
                    <span className="text-muted-foreground block truncate text-xs">
                      {[c.currentRole, c.email].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </span>
                  {on && (
                    <span className="text-muted-foreground flex shrink-0 items-center gap-1 text-xs">
                      <Check className="size-3.5" /> On this shortlist
                    </span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
        <PlacementOptions
          pipeline={pipeline}
          stageId={stageId}
          onStage={setStageId}
          visible={visible}
          onVisible={setVisible}
        />
        {add.isError && (
          <p className="text-danger text-sm" role="alert">
            {describeError(add.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => add.mutate()}
            disabled={picked.size === 0 || add.isPending}
            data-testid="add-picked"
          >
            <UserPlus /> Add {picked.size || ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Candidate side → "Add to job" (TS-1599), for one candidate or a table selection: find the
 * job, pick its shortlist (the default batch first), the stage and client visibility.
 */
export function AddToJobDialog({
  candidateIds,
  label,
  pipeline,
  onClose,
  onDone,
}: {
  candidateIds: readonly string[];
  /** "Ada Lovelace" or "12 candidates", for the description. */
  label: string;
  pipeline: PipelineView | undefined;
  onClose: () => void;
  onDone: (result: AddResult, jobTitle: string) => void;
}) {
  const [q, setQ] = useState('');
  const query = useDebounced(q.trim(), 250);
  const [job, setJob] = useState<JobView | null>(null);
  const [shortlistId, setShortlistId] = useState('');
  const [stageId, setStageId] = useState('');
  const [visible, setVisible] = useState(false);
  const jobs = useQuery({
    queryKey: ['job-picker', query],
    queryFn: () => api.jobs.search({ limit: 20, ...(query ? { q: query } : {}) }),
  });
  const shortlists = useQuery({
    queryKey: ['job-shortlists', job?.id],
    queryFn: () => api.jobs.shortlists(job!.id),
    enabled: job !== null,
  });
  const lists = shortlists.data ?? [];
  const target = shortlistId || lists.find((s) => s.isDefault)?.id || lists[0]?.id || '';
  const add = useMutation({
    mutationFn: () => addCandidates(target, candidateIds, { stageId, visibleToClient: visible }),
    onSuccess: (r) => onDone(r, job!.title),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add to a job</DialogTitle>
          <DialogDescription>Put {label} on a job&apos;s shortlist.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2" />
          <Input
            autoFocus
            aria-label="Search jobs"
            placeholder="Search jobs by title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10"
          />
        </div>
        <ul
          className="border-border max-h-60 divide-y overflow-y-auto rounded-xl border"
          data-testid="job-picker"
        >
          {jobs.isPending && (
            <li className="text-muted-foreground flex items-center gap-2 px-4 py-6 text-sm">
              <Loader2 className="size-4 animate-spin" /> Searching…
            </li>
          )}
          {jobs.data && jobs.data.items.length === 0 && (
            <li className="text-muted-foreground px-4 py-6 text-center text-sm">No jobs match.</li>
          )}
          {jobs.data?.items.map((j) => (
            <li key={j.id}>
              <button
                type="button"
                onClick={() => {
                  setJob(j);
                  setShortlistId('');
                }}
                className={cn(
                  'hover:bg-surface-hover flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors',
                  job?.id === j.id && 'bg-brand-soft/60',
                )}
                aria-pressed={job?.id === j.id}
              >
                <Briefcase className="text-muted-foreground size-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{j.title}</span>
                  <span className="text-muted-foreground block truncate text-xs">
                    {j.project?.name ?? ''}
                  </span>
                </span>
                {job?.id === j.id && <Check className="text-brand size-4 shrink-0" />}
              </button>
            </li>
          ))}
        </ul>
        {job && lists.length > 1 && (
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Shortlist
            <Select value={target} onValueChange={setShortlistId}>
              <SelectTrigger aria-label="Shortlist">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {lists.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        )}
        <PlacementOptions
          pipeline={pipeline}
          stageId={stageId}
          onStage={setStageId}
          visible={visible}
          onVisible={setVisible}
        />
        {add.isError && (
          <p className="text-danger text-sm" role="alert">
            {describeError(add.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => add.mutate()}
            disabled={!job || !target || add.isPending}
            data-testid="add-to-job"
          >
            <UserPlus /> Add to {job ? job.title : 'job'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
